import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { ILoginForm, IUser } from "common/interface";
import { IWhDivision } from "common/interface/IUser";
import { IApiError } from "common/interface/IApiError";
import { IInitConfig, IPlatformConfigResponse } from "common/interface/IInitConfig";
import { getPersistedUser, persistUser, removePersistedUser } from "services/PersistService/PersistUserService";
import { getPersistedInitConfig, persistInitConfig, removePersistedInitConfig } from "services/PersistService/PersistInitConfigService";
import { getWarehouseId, removeWarehouseId, setWarehouseId } from "services/PersistService/PersistWareHouseReturn";
import { getWhDivisionId, removeWhDivisionId, setWhDivisionId } from "services/PersistService/PersistDivisionService";

const APP_VERSION = process.env.REACT_APP_VERSION;

interface State {
  version?: string;
  apiDomain?: string;
  siderCollapsed?: boolean;
  whId?: number;
  whDivId?: number;
  whDivisions: IWhDivision[];
  /** Warehouse id of the division fetch currently in flight. Older responses are ignored. */
  divisionsRequestWhId?: number;
  divisionsStatus: "idle" | "loading" | "succeeded" | "failed";
  user: IUser;
  initConfig?: IInitConfig;
  initConfigStatus: "idle" | "loading" | "succeeded" | "failed";
  status: "idle" | "loading" | "succeeded" | "failed";
  errors?: string[];
  otpStatus: "idle" | "loading" | "succeeded" | "failed";
  otpError?: string;
}

const initialState: State = {
  version: APP_VERSION,
  siderCollapsed: undefined,
  user: getPersistedUser() || {},
  initConfig: getPersistedInitConfig(),
  status: "idle",
  whId: getWarehouseId(),
  whDivId: getWhDivisionId(),
  whDivisions: [],
  divisionsStatus: "idle",
  initConfigStatus: "idle",
  otpStatus: "idle",
};

export const login = createAsyncThunk<IUser & IApiError, ILoginForm, {}>("app/login", async (user, thunkApi) => {
  const response = await API.post(apis.login, {
    username: `${user.mobile}`,
    password: user.password
  }, {
    headers: {
      "Content-Type": "application/json"
    }
  })
    .then(success => success.data)
    .catch(error => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const generateLoginOtp = createAsyncThunk<void, string, {}>(
  "app/generateLoginOtp",
  async (mobile, thunkApi) => {
    return API.post(apis.generateOtp, { mobile })
      .then(() => undefined)
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Failed to send OTP. Please try again.";
        return thunkApi.rejectWithValue({ message: errorMessage });
      });
  }
);

interface LoginWithOtpParams {
  mobile: string;
  otp: string;
}

export const loginWithOtp = createAsyncThunk<IUser & IApiError, LoginWithOtpParams, {}>(
  "app/loginWithOtp",
  async ({ mobile, otp }, thunkApi) => {
    return API.post(apis.verifyOtp, { mobile, otp, deviceId: "wms-web-portal" })
      .then((response) => response.data)
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Invalid OTP. Please try again.";
        return thunkApi.rejectWithValue({ message: errorMessage });
      });
  }
);

export const logoutUser = createAsyncThunk("app/logoutUser", async (_, thunkApi) => {
  await API.post(apis.logout).catch(() => {
    // Silently ignore API errors — local logout always proceeds
  });

  thunkApi.dispatch(logout());
});

/**
 * Global platform config — fetched once on every app load for logged-in users
 * (dispatched from LoggedInLayout) and persisted to localStorage so config
 * values can be read anywhere without re-fetching.
 * Source: GET /api/v1/config/get/wms-web (replaces legacy /api/v1/init).
 */
export const fetchInitConfig = createAsyncThunk<IInitConfig>(
  "app/fetchInitConfig",
  async () => {
    const success = await API.get(apis.initConfig);
    const data = success.data as IPlatformConfigResponse;
    // Config API wraps values in `{ name, version, config }`.
    if (data?.config) {
      return data.config;
    }
    return data as unknown as IInitConfig;
  }
);

export const fetchWhDivisions = createAsyncThunk<IWhDivision[], number>(
  "app/fetchWhDivisions",
  async (whId) => {
    // Fetch every division once and keep only the ones for the selected
    // warehouse. Same flow as before, just a different source endpoint.
    const response = await API.get(apis.fetchAllWhDivisions);
    const all = (response.data as IWhDivision[]) ?? [];
    return all.filter((division) => division.whId === whId);
  }
);

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    toggleSider: (state) => {
      state.siderCollapsed = !state.siderCollapsed;
    },
    setSider: (state, action) => {
      state.siderCollapsed = !action.payload;
    },
    logout: (state) => {
      state.user = {};
      state.whId = undefined;
      state.whDivId = undefined;
      state.whDivisions = [];
      state.divisionsStatus = "idle";
      state.initConfig = undefined;
      removeWarehouseId();
      removeWhDivisionId();
      removePersistedUser();
      removePersistedInitConfig();
    },
    updateWarehouseId: (state, action) => {
      const newWhId = action.payload;
      // Clear only the selected division ID when warehouse changes;
      // whDivisions is left intact so the header can still resolve the
      // division name after Proceed — fetchWhDivisions.pending clears it
      // whenever a fresh fetch is triggered (e.g. on handleWarehouseChange).
      if (Number(newWhId) !== Number(state.whId)) {
        state.whDivId = undefined;
        removeWhDivisionId();
      }
      state.whId = newWhId;
      if (newWhId === undefined) {
        removeWarehouseId();
      } else {
        setWarehouseId(newWhId);
      }
    },
    updateWhDivisionId: (state, action) => {
      state.whDivId = action.payload;
      if (action.payload === undefined) {
        removeWhDivisionId();
      } else {
        setWhDivisionId(action.payload);
      }
    },
    resetLoginState: (state) => {
      state.status = "idle";
      state.errors = undefined;
      state.otpStatus = "idle";
      state.otpError = undefined;
    },
  },
  extraReducers(builder) {
    const applyLoginResult = (state: State, payload: IUser & IApiError) => {
      state.status = "succeeded";
      const { errors, error, message, ...userData } = payload;

      if (userData && userData.authToken) {
        state.user = { ...userData, isAuthenticated: true };
        state.errors = [];
        persistUser(state.user);
      } else {
        state.status = "failed";
        state.errors = ["Something went wrong!"];
      }
    };

    const applyLoginError = (state: State, action: { payload: unknown }) => {
      const error = action.payload as IApiError;
      state.status = "failed";

      if (error?.message) {
        state.errors = [error.message];
      }
    };

    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        applyLoginResult(state, action.payload);
      })
      .addCase(login.rejected, (state, action) => {
        applyLoginError(state, action);
      })
      .addCase(generateLoginOtp.pending, (state) => {
        state.otpStatus = "loading";
        state.otpError = undefined;
      })
      .addCase(generateLoginOtp.fulfilled, (state) => {
        state.otpStatus = "succeeded";
      })
      .addCase(generateLoginOtp.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.otpStatus = "failed";
        state.otpError = error?.message || "Failed to send OTP. Please try again.";
      })
      .addCase(loginWithOtp.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loginWithOtp.fulfilled, (state, action) => {
        applyLoginResult(state, action.payload);
      })
      .addCase(loginWithOtp.rejected, (state, action) => {
        applyLoginError(state, action);
      })
      .addCase(fetchWhDivisions.pending, (state, action) => {
        state.divisionsStatus = "loading";
        state.whDivisions = [];
        state.divisionsRequestWhId = action.meta.arg;
      })
      .addCase(fetchWhDivisions.fulfilled, (state, action) => {
        // A slower response for a warehouse the user already left must not
        // replace the list. That replacement was resetting the form to the
        // previously saved division.
        if (action.meta.arg !== state.divisionsRequestWhId) return;
        state.divisionsStatus = "succeeded";
        state.whDivisions = action.payload;
      })
      .addCase(fetchWhDivisions.rejected, (state, action) => {
        if (action.meta.arg !== state.divisionsRequestWhId) return;
        state.divisionsStatus = "failed";
        state.whDivisions = [];
      })
      .addCase(fetchInitConfig.pending, (state) => {
        state.initConfigStatus = "loading";
      })
      .addCase(fetchInitConfig.fulfilled, (state, action) => {
        state.initConfigStatus = "succeeded";
        state.initConfig = action.payload;
        persistInitConfig(action.payload);
      })
      .addCase(fetchInitConfig.rejected, (state) => {
        // Keep any previously persisted config so the session can still use it
        state.initConfigStatus = "failed";
      });
  },
});

export const { toggleSider, setSider, logout, updateWarehouseId, updateWhDivisionId, resetLoginState } = appSlice.actions;
export default appSlice.reducer;
